import { useState, useMemo, useCallback, useEffect } from 'react';
import { addMonths, addWeeks } from 'date-fns';
import { Plus } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '@/shared/lib/toast';
import { Avatar } from '@/shared/ui/Avatar';
import { ListPagination } from '@/shared/ui/ListPagination';
import { AppointmentFilterBar } from './AppointmentFilterBar';
import { AppointmentTable } from './AppointmentTable';
import { AppointmentBulkBar } from './AppointmentBulkBar';
import { AppointmentActionsMenu } from './AppointmentActionsMenu';
import { AppointmentStatusPill } from './AppointmentStatusPill';
import { RescheduleAppointmentModal } from './RescheduleAppointmentModal';
import {
  useAppointmentList,
  useUpdateAppointment,
  useCancelAppointment,
  useTransitionStatus,
  useSyncedWorkingDayDate,
} from '@/features/appointments';
import type { Appointment } from '@/features/appointments';
import { computeRange, type ViewMode } from './ViewModeSwitcher';

const FILTER_TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'cancelled', label: 'Cancelled' },
];

const STATUS_FILTER_MAP: Record<string, string> = {
  upcoming: 'scheduled,confirmed,checked_in,in_progress',
  past: 'completed',
  cancelled: 'cancelled,no_show',
  all: 'scheduled,confirmed,checked_in,in_progress,completed',
};

const VALID_TABS = new Set(Object.keys(STATUS_FILTER_MAP));

function tabFromSearchParams(searchParams: URLSearchParams): string {
  const tab = searchParams.get('tab')?.trim() ?? '';
  return VALID_TABS.has(tab) ? tab : 'upcoming';
}

const PAGE_SIZE = 50;

export function AppointmentListPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => tabFromSearchParams(searchParams));
  const [search, setSearch] = useState(() => searchParams.get('search')?.trim() ?? '');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>('scheduled_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [selectedDate, setSelectedDate] = useSyncedWorkingDayDate();
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const [referenceDate, setReferenceDate] = useState<Date | undefined>();
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);

  useEffect(() => {
    const dateParam = searchParams.get('date')?.trim();
    if (dateParam) {
      setSelectedDate(dateParam);
      setViewMode('day');
      setDateFrom(undefined);
      setDateTo(undefined);
      setReferenceDate(undefined);
    }
    setActiveTab(tabFromSearchParams(searchParams));
  }, [searchParams, setSelectedDate]);

  const statusValue = STATUS_FILTER_MAP[activeTab] ?? 'scheduled,confirmed,checked_in,in_progress';

  useEffect(() => {
    setPage(1);
  }, [activeTab, search, sortBy, sortOrder, dateFrom, dateTo, selectedDate, viewMode]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page]);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    const range = computeRange(mode);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setReferenceDate(range.referenceDate);
    if (mode === 'day') {
      setSelectedDate(range.dateFrom);
    }
  }, []);

  const handleNavigateRange = useCallback((direction: -1 | 1) => {
    if (!referenceDate) {
      const range = computeRange(viewMode);
      setDateFrom(range.dateFrom);
      setDateTo(range.dateTo);
      setReferenceDate(range.referenceDate);
      return;
    }
    const nextRef = viewMode === 'month'
      ? addMonths(referenceDate, direction)
      : addWeeks(referenceDate, direction);
    const range = computeRange(viewMode, nextRef);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setReferenceDate(range.referenceDate);
    if (viewMode === 'day') {
      setSelectedDate(range.dateFrom);
    }
  }, [viewMode, referenceDate]);

  const handleToday = useCallback(() => {
    const range = computeRange(viewMode);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setReferenceDate(range.referenceDate);
    setSelectedDate(range.dateFrom);
  }, [viewMode]);

  const apiParams = useMemo(() => {
    const base = {
      status: statusValue,
      search: search || undefined,
      sortBy: sortBy as 'scheduled_at' | 'created_at' | 'status',
      sortOrder,
      page,
      limit: PAGE_SIZE,
    };
    if (dateFrom && dateTo) {
      return { ...base, dateFrom, dateTo };
    }
    return { ...base, date: selectedDate };
  }, [statusValue, search, sortBy, sortOrder, page, dateFrom, dateTo, selectedDate]);

  const { data, isLoading, isError } = useAppointmentList(apiParams, { keepPrevious: true });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(Math.max(1, totalPages));
  }, [page, totalPages]);

  const { mutate: transitionStatus } = useTransitionStatus(dateFrom ?? selectedDate);
  const { mutate: updateAppt } = useUpdateAppointment();
  const { mutate: cancelAppt } = useCancelAppointment();

  const handleStatusChange = useCallback((id: string, action: 'confirm' | 'check-in' | 'start' | 'complete' | 'no-show') => {
    transitionStatus({ id, action }, {
      onSuccess: () => toast.success('Status updated'),
      onError: (err: unknown) => {
        const error = err as { response?: { data?: { error?: { message?: string } } } };
        toast.failure(error?.response?.data?.error?.message ?? 'Failed to update status');
      },
    });
  }, [transitionStatus]);

  const handleEdit = useCallback((id: string) => {
    navigate(`/appointments/${id}/edit`);
  }, [navigate]);

  const handleReschedule = useCallback((id: string) => {
    setRescheduleId(id);
  }, []);

  const columns = useMemo(() => [
    {
      key: 'patient' as const,
      header: 'Patient',
      className: 'font-semibold text-[var(--text-primary)]',
      render: (row: Appointment) => {
        const name = row.patientName ?? 'Patient';
        return (
          <div className="flex items-center gap-3">
            <Avatar name={name} size="sm" />
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold text-ink-900">{name}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'provider' as const,
      header: 'Doctor',
      className: 'text-ink-700',
      render: (row: Appointment) => {
        const name = row.providerName ?? null;
        if (!name) return <span className="text-ink-400">—</span>;
        return (
          <div className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] bg-surface-50 px-2.5 py-1">
            <Avatar name={name} size="xs" />
            <span className="text-[12px] font-medium text-ink-700">{name}</span>
          </div>
        );
      },
    },
    {
      key: 'scheduled_at' as const,
      header: 'Time',
      className: 'font-semibold text-[var(--text-primary)]',
      render: (row: Appointment) => {
        const d = new Date(row.scheduledAt);
        return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
      },
    },
    {
      key: 'duration_minutes' as const,
      header: 'Duration',
      className: 'text-[var(--text-secondary)]',
      render: (row: Appointment) => `${row.durationMinutes} min`,
    },
    {
      key: 'appointment_type' as const,
      header: 'Type',
      className: 'text-[var(--text-secondary)] capitalize',
      render: (row: Appointment) => row.appointmentType.replace(/_/g, ' '),
    },
    {
      key: 'status' as const,
      header: 'Status',
      render: (row: Appointment) => <AppointmentStatusPill status={row.status} />,
    },
  ], []);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  const handleCancel = useCallback((id: string) => {
    cancelAppt({ id, input: { reason: 'other' } }, {
      onSuccess: () => toast.success('Appointment cancelled'),
      onError: () => toast.failure('Failed to cancel appointment'),
    });
  }, [cancelAppt]);

  const actionsColumn = useMemo(() => ({
    key: 'actions' as const,
    header: '',
    className: 'w-10',
    render: (row: Appointment) => (
      <AppointmentActionsMenu
        status={row.status}
        onViewDetail={() => navigate(`/appointments/${row.id}`)}
        onEdit={() => handleEdit(row.id)}
        onReschedule={() => handleReschedule(row.id)}
        onConfirm={() => handleStatusChange(row.id, 'confirm')}
        onCheckIn={() => handleStatusChange(row.id, 'check-in')}
        onStart={() => handleStatusChange(row.id, 'start')}
        onCheckOut={() => handleStatusChange(row.id, 'complete')}
        onCancel={() => handleCancel(row.id)}
        onNoShow={() => handleStatusChange(row.id, 'no-show')}
      />
    ),
  }), [handleEdit, handleReschedule, handleStatusChange, handleCancel, navigate]);

  const allColumns = useMemo(() => [...columns, actionsColumn], [columns, actionsColumn]);

  const handleBulkConfirm = () => {
    selectedIds.forEach((id) => {
      updateAppt({ id, input: { status: 'confirmed' } as unknown as Record<string, unknown>, version: 0 });
    });
    toast.success(`Confirming ${selectedIds.size} appointments`);
    setSelectedIds(new Set());
  };

  const handleBulkCancel = () => {
    selectedIds.forEach((id) => {
      cancelAppt({ id, input: { reason: 'other' } });
    });
    toast.success(`Cancelling ${selectedIds.size} appointments`);
    setSelectedIds(new Set());
  };

  const handleBulkRemind = () => {
    toast.success(`Sending reminders to ${selectedIds.size} patients`);
    setSelectedIds(new Set());
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[28px] font-semibold tracking-tight text-[var(--text-primary)]">
            Appointments
          </h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {data?.total ?? 0} total appointments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/appointments/calendar"
            className="rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--bg-surface-muted)]"
          >
            Calendar
          </Link>
          <Link
            to="/appointments/new"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            <Plus className="size-4" />
            New Appointment
          </Link>
        </div>
      </div>

      <AppointmentFilterBar
        tabs={FILTER_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        search={search}
        onSearchChange={setSearch}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onNavigateRange={handleNavigateRange}
        onToday={handleToday}
      />

      <AppointmentBulkBar
        selectedCount={selectedIds.size}
        onMarkConfirmed={handleBulkConfirm}
        onSendReminder={handleBulkRemind}
        onCancel={handleBulkCancel}
      />

      <AppointmentTable
        data={data?.data ?? []}
        columns={allColumns}
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No appointments match your filters"
        onRowClick={(id) => navigate(`/appointments/${id}`)}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        footer={
          <ListPagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            itemLabel="appointments"
          />
        }
      />

      <RescheduleAppointmentModal
        appointmentId={rescheduleId}
        open={!!rescheduleId}
        onOpenChange={(open) => {
          if (!open) setRescheduleId(null);
        }}
      />
    </section>
  );
}
